import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import Head from 'next/head';

import { trackResultPageView } from '@/utils/tracking/finnAnalytics';
import { setInitialBannerFilterValues, updateBannerFilterValues } from 'shared/banners/bannerParams';
import { useInterval } from '@/hooks/useInterval';
import { fetchHotels, pollHotels } from '@/ducks/searchResults/actions';
import { actions as filterActions } from '../../state/filterActions';
import { actions as mapActions, pollHotelPageForMap } from '../../state/mapActions';
import type { AccommodationState, Filters, MapBounds, MapConfig, MapState, SearchConfig, SearchDestination } from '@/interfaces';
import { SearchResultsLayout } from './containers/searchResultsLayout/SearchResultsLayout';
import { SearchResultsMapLayout } from './containers/searchResultsMapLayout/SearchResultsMapLayout';
import { filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';
import { updateSearchResultsPageQueryParams } from '@/utils/url/routerHelpers';
import { recordSessionTrafficSource } from '@/utils/tracking/sourceCookie';
import { useFavoriteHotelsAPI } from '@/hooks/favoriteHotelsAPI';
import ThemePage from './containers/themePage/ThemePage';
import FavoritesPage from './containers/favoritesPage/FavoritesPage';

const {
    publicRuntimeConfig: { basePath },
} = getConfig();

interface StateProps {
    numberOfHotels: number;
    isComplete: boolean;
    filters: Filters;
    destination: SearchDestination;
    mapState: MapState;
    searchConfig: SearchConfig;
}

interface DispatchProps {
    triggerPoll: () => void;
    triggerFetch: (resetMap: boolean) => void;
    setMapState: (config: MapConfig) => void;
    triggerMapHotelPagePoll: (pageNumber: number) => void;
    setMapBounds: (mapBounds: MapBounds) => void;
    setPreferredHotels: (hotelIds: readonly number[]) => void;
}

type Props = StateProps &
    DispatchProps & {
        filterErrors: readonly string[];
    };

const SearchResultsPage = ({
    filterErrors,
    numberOfHotels,
    isComplete,
    destination,
    filters,
    triggerPoll,
    triggerFetch,
    triggerMapHotelPagePoll,
    mapState,
    setMapState,
    setMapBounds,
    setPreferredHotels,
    searchConfig,
}: Props) => {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [previousQuery, setPreviousQuery] = useState('');
    const favoriteHotels = useFavoriteHotelsAPI();

    const isSeoIndexable = () => {
        function hasAcceptableFilter(filter: string) {
            const acceptableFilters = ['rooms', 'sort', 'sortDirection'];
            return acceptableFilters.includes(filter);
        }

        const activeFilters = new URLSearchParams(filtersToResultPageBrowserQueryParams(filters, false));
        for (const key of activeFilters) {
            if (!hasAcceptableFilter(key[0])) {
                return false;
            }
        }
        return true;
    };

    useEffect((): void => {
        recordSessionTrafficSource(window.location.search);
        updateSearchResultsPageQueryParams(mapState.open, filters, searchConfig);
    }, []);

    useEffect((): void => {
        trackResultPageView(filters, mapState.open, isComplete ? numberOfHotels : undefined, destination.name);
    }, [mapState.open]);

    useEffect((): void => {
        if (favoriteHotels?.hotelIds.length > 0) {
            setPreferredHotels(favoriteHotels?.hotelIds.slice(0, 30));
        }
    }, [favoriteHotels?.hotelIds]);

    useEffect((): void => {
        setInitialBannerFilterValues({
            ...filters,
            destination: {
                city: destination?.name,
                country: destination?.hierarchy[0]?.name,
            },
        });
    }, [filters.page]);

    useEffect(() => {
        if (isComplete && filtersToResultPageBrowserQueryParams(filters, false) !== previousQuery) {
            setPreviousQuery(filtersToResultPageBrowserQueryParams(filters, false));
            trackResultPageView(filters, mapState.open, numberOfHotels, destination.name);
            updateBannerFilterValues({
                ...filters,
                destination: {
                    city: destination?.name,
                    country: destination?.hierarchy[0]?.name,
                },
            });
        }
    }, [isComplete, filtersToResultPageBrowserQueryParams(filters, false)]);

    useInterval(
        (): void => {
            if (!isComplete) {
                triggerPoll();
            }
        },
        !isComplete ? 2000 : null,
    );

    useInterval(
        (): void => {
            if (mapState.open) {
                mapState.hotels.forEach((page, index) => {
                    if (!page.isComplete) {
                        triggerMapHotelPagePoll(index);
                    }
                });
            }
        },
        mapState.open && mapState.hotels.some((hotel) => !hotel.isComplete) ? 2000 : null,
    );

    const destinationName = destination?.name ?? filters.destination;

    const title = `Alle hotell i ${destinationName} | FINN reise`;
    const description = `Søk etter det beste eller billigste hotellet i ${destinationName}. Ønsker du svømmebasseng, spa eller beliggenhet i sentrum? Søk og bestill her!`;
    const canonical = `https://finn.no${basePath}${`/resultat/${filters.destination}/`.toLowerCase()}?rooms=2&sort=popularity&sortDirection=descending`;

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:description" content={description} />
                <meta property="og:title" content={title} />
                <meta property="og:image" content="https://www.finn.no/assets/finn-logo-large.png" />
                <meta property="og:url" content={canonical} />
                {!isSeoIndexable() && <meta name="robots" content="noindex, nofollow" />}
                <link rel="canonical" href={canonical} />
            </Head>
            {mapState.open ? (
                <SearchResultsMapLayout
                    filters={filters}
                    filterErrors={filterErrors}
                    mapState={mapState}
                    setMapState={setMapState}
                    filtersOpen={filtersOpen}
                    setFiltersOpen={setFiltersOpen}
                    numberOfHotels={numberOfHotels}
                    setMapBounds={setMapBounds}
                    triggerFetch={triggerFetch}
                    searchConfig={searchConfig}
                />
            ) : (
                <SearchResultsLayout
                    filterErrors={filterErrors}
                    numberOfHotels={numberOfHotels}
                    setMapState={setMapState}
                    filtersOpen={filtersOpen}
                    setFiltersOpen={setFiltersOpen}
                    filters={filters}
                    destination={destination}
                    searchConfig={searchConfig}
                />
            )}
        </>
    );
};

SearchResultsPage.ThemePage = ThemePage;
SearchResultsPage.FavoritesPage = FavoritesPage;

const mapStateToProps = (state: AccommodationState): StateProps => ({
    numberOfHotels: state.searchResults.totalFilteredResults || 0,
    isComplete: state.searchResults.isComplete,
    destination: state.searchResults.destination,
    searchConfig: state.searchResults.searchConfig,
    filters: state.filters,
    mapState: state.map,
});

const mapDispatchToProps = {
    triggerPoll: pollHotels,
    triggerFetch: fetchHotels,
    triggerMapHotelPagePoll: pollHotelPageForMap,
    setMapState: mapActions.setMapState,
    setMapBounds: filterActions.changeMapBounds,
    setPreferredHotels: filterActions.setPreferredHotels,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsPage);
